import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getPaymentStatus } from "@/lib/api";

const RED = "#e11d2a";

/**
 * Page post-paiement de l'abonnement : Stripe renvoie ici avec ?session_id=...
 * On vérifie que le paiement est bien "paid", puis on propose de créer le compte
 * (email/mot de passe OU Google). La session Stripe payée débloque la création.
 */
export default function CreateAccount() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { register, startGoogleLogin, isAuthenticated } = useAuth();

  const [phase, setPhase] = useState("checking"); // checking | ready | failed
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const polls = useRef(0);

  useEffect(() => { if (isAuthenticated) nav("/hub", { replace: true }); }, [isAuthenticated, nav]);

  useEffect(() => {
    if (!sessionId) { setPhase("failed"); return; }
    let active = true;
    const check = async () => {
      try {
        const st = await getPaymentStatus(sessionId);
        if (!active) return;
        if (st.payment_status === "paid") { setPhase("ready"); return; }
        if (["expired", "failed"].includes(st.payment_status)) { setPhase("failed"); return; }
      } catch { /* retry */ }
      polls.current += 1;
      if (polls.current > 25) { setPhase("failed"); return; }
      setTimeout(check, 2000);
    };
    check();
    return () => { active = false; };
  }, [sessionId]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await register({ email: email.trim(), password, name: name.trim() || undefined, session_id: sessionId });
      nav(u && !u.profile_completed ? "/profil" : "/hub", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue.";
      setError(typeof msg === "string" ? msg : "Une erreur est survenue.");
    } finally { setBusy(false); }
  };

  const googleCreate = () => {
    if (sessionId) localStorage.setItem("td_pending_checkout", sessionId);
    startGoogleLogin();
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.brandRow}><span style={s.kanji}>天</span><span style={s.brand}>TENGOKU <span style={{ color: RED }}>DŌJŌ</span></span></div>

        {phase === "checking" && (
          <div style={s.center} data-testid="createaccount-checking">
            <div style={s.spinner} />
            <p style={s.muted}>Confirmation de ton paiement…</p>
          </div>
        )}

        {phase === "failed" && (
          <div style={s.center} data-testid="createaccount-failed">
            <p style={s.err}>Nous n'avons pas pu confirmer ton paiement.</p>
            <Link to="/inscription" style={s.retry}>Réessayer l'abonnement</Link>
          </div>
        )}

        {phase === "ready" && (
          <div data-testid="createaccount-ready">
            <div style={s.paid}>✓ Paiement confirmé — bienvenue au Dōjō !</div>
            <p style={s.lead}>Crée ton compte pour entrer.</p>

            <button onClick={googleCreate} style={s.google} type="button" data-testid="createaccount-google">
              <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 10 }}>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Créer mon compte avec Google
            </button>
            <div style={s.divider}><span style={s.line} /><span style={s.dtext}>ou</span><span style={s.line} /></div>

            <form onSubmit={submit} style={s.form}>
              <div style={s.field}><label style={s.label}>Nom</label><input style={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" data-testid="createaccount-name" /></div>
              <div style={s.field}><label style={s.label}>Email</label><input style={s.input} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@email.com" data-testid="createaccount-email" /></div>
              <div style={s.field}><label style={s.label}>Mot de passe</label><input style={s.input} type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="createaccount-password" /></div>
              {error && <div style={s.errBox} data-testid="createaccount-error">{error}</div>}
              <button type="submit" disabled={busy} style={{ ...s.submit, opacity: busy ? 0.6 : 1 }} data-testid="createaccount-submit">
                {busy ? "Création…" : "Créer mon compte"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "#fff", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Inter', system-ui, sans-serif" },
  card: { width: "100%", maxWidth: 440, background: "#fff", border: "1px solid #ededed", borderRadius: 20, boxShadow: "0 20px 60px rgba(0,0,0,0.08)", padding: "36px 32px" },
  brandRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 24 },
  kanji: { fontSize: 34, fontWeight: 700 },
  brand: { fontSize: 20, fontWeight: 800, letterSpacing: 3 },
  center: { display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "20px 0" },
  spinner: { width: 34, height: 34, borderRadius: "50%", border: "3px solid #eee", borderTopColor: RED, animation: "td-spin 0.8s linear infinite" },
  muted: { color: "#888", fontSize: 14 },
  err: { color: RED, fontSize: 14.5, fontWeight: 600, textAlign: "center" },
  retry: { color: RED, fontWeight: 700, textDecoration: "none", marginTop: 8 },
  paid: { background: "#e8f7ec", color: "#1a7f37", border: "1px solid #b7e4c4", padding: "11px 14px", borderRadius: 10, fontWeight: 700, fontSize: 14, textAlign: "center" },
  lead: { textAlign: "center", color: "#555", fontSize: 14.5, margin: "16px 0 18px" },
  google: { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 0", border: "1.5px solid #e2e2e2", borderRadius: 10, background: "#fff", color: "#222", fontWeight: 600, cursor: "pointer", fontSize: 14 },
  divider: { display: "flex", alignItems: "center", gap: 12, margin: "18px 0" },
  line: { flex: 1, height: 1, background: "#eee" },
  dtext: { fontSize: 12, color: "#aaa" },
  form: { display: "flex", flexDirection: "column", gap: 15 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: "#444" },
  input: { padding: "12px 14px", border: "1.5px solid #e0e0e0", borderRadius: 10, fontSize: 14, outline: "none", color: "#111" },
  errBox: { background: `${RED}11`, border: `1px solid ${RED}44`, color: RED, padding: "10px 12px", borderRadius: 10, fontSize: 13.5 },
  submit: { padding: "13px 0", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 15, boxShadow: `0 6px 18px ${RED}44` },
};

if (typeof document !== "undefined" && !document.getElementById("td-spin-kf")) {
  const el = document.createElement("style");
  el.id = "td-spin-kf";
  el.textContent = "@keyframes td-spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(el);
}
